import React, { FC, Fragment } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  Theme
} from '@mui/material';
import { ExpandMore, InsertDriveFileOutlined } from '@mui/icons-material';
// Types
import { formatDate, formatMoney } from '../../../helpers';
import { IInvoicePayment } from '../../../models';
import { Link } from '../../../components';
import { LoadingButton } from '@mui/lab';

interface IPaymentsMobile {
  original: IInvoicePayment;
  isAdmin: boolean;
  handleDownloadPDF: (val: IInvoicePayment) => void;
  isDownloading: {
    [id: string | number]: boolean;
  };
}

export const PaymentsMobile: FC<IPaymentsMobile> = ({
  original,
  isAdmin,
  handleDownloadPDF,
  isDownloading
}) => {
  const classes = useStyles();
  const { date, type, number, amount, vendor, lotNumbers, hasAchSummaryGenerated } = original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography className={classes.activeName} variant="body2">
            {type}
          </Typography>
          <Divider orientation="vertical" flexItem className={classes.divider} />
          <Typography className={classes.activeName} variant="body2">
            {number}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Type:
          </FormLabel>
          <Typography component="span" variant="body2">
            {type}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Check No.:
          </FormLabel>
          <Link
            className={classes.link}
            to={`/vendors/payments/${number}?type=${type.replace(' ', '')}&vendorId=${
              vendor.vendorId
            }`}
          >
            <Typography component="span" variant="body2">
              {number}
            </Typography>
          </Link>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Date:
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatDate(date)}
          </Typography>
        </div>
        {isAdmin && (
          <>
            <div className={classes.panelSummaryWrapper}>
              <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
                Vendor:
              </FormLabel>
              <Typography component="span" variant="body2">
                {vendor.name}
              </Typography>
            </div>
            <div className={classes.panelSummaryWrapper}>
              <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
                Subcontractor Type:
              </FormLabel>
              <Typography component="span" variant="body2">
                {vendor.subType}
              </Typography>
            </div>
          </>
        )}
        {lotNumbers && lotNumbers.length > 0 && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Lot(s):
            </FormLabel>
            <Typography component="span" variant="body2">
              {lotNumbers.map((lotNumber, index) => {
                const seperator = index !== 0 ? ', ' : '';
                return (
                  <Fragment key={index}>
                    {seperator}
                    {lotNumber}
                  </Fragment>
                );
              })}
            </Typography>
          </div>
        )}
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Total:
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatMoney(amount)}
          </Typography>
        </div>
        {!!hasAchSummaryGenerated && (
          <div className={classes.panelSummaryWrapper}>
            <LoadingButton
              loading={isDownloading[original.batchNumber]}
              loadingPosition="start"
              color="primary"
              startIcon={<InsertDriveFileOutlined />}
              disabled={isDownloading[original.batchNumber]}
              onClick={() => handleDownloadPDF(original)}
            >
              <strong>PDF</strong>
            </LoadingButton>
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    margin: theme.spacing(0.25, 0),
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  link: {
    fontSize: theme.spacing(1)
  },
  activeName: {
    color: theme.palette.grey[500]
  },
  divider: {
    margin: '0 5px'
  }
}));
